import moment from 'moment';
import { listIcon } from '../../../../styles/icons';
import { PRODUCT_TYPE } from '../../../../utils';
import {
  ActionCellRenderer,
  ActionTypes,
} from '../../../../utils/custom-renderer/action-cell-renderer';
import { IconCellRendered } from '../../../../utils/custom-renderer/icon-cell-renderer';
import { formatCodDate, getFormattedDate, getFormattedNumber } from '../../../../utils/formatting';
import {
  filterGreaterOrEqualNumberic,
  filterLessOrEqualNumberic,
  filterNumericRange,
} from '../../../../utils/grid';
import type { RFQSelector } from './rfq-selector';

export enum COLUMN_FIELDS {
  TICKER = 'RFQ.TICKER',
  TECHNOLOGY = 'RFQ.TECHNOLOGY',
  UNIQUE_ID = 'RFQ.UNIQUE_ID',
  ISO = 'RFQ.ISO',
  SETTLEMENT_POINT = 'RFQ.SETTLEMENT_POINT',
  TARGET_COD = 'RFQ.TARGET_COD',
  VOLUME = 'RFQ.REQUESTED_VOLUME',
  TENOR_START_DATE = 'RFQ.TENOR_START_DATE',
  TENOR_MONTHS = 'RFQ.TENOR_MONTHS',
  ACTIONS = 'ACTIONS',
}

export function RFQ_COLUMNS(context: RFQSelector) {
  return [
    {
      field: 'SELECT',
      headerName: '',
      suppressMenu: true,
      headerCheckboxSelection: false,
      width: 70,
      checkboxSelection: true,
      cellClass: (params) => {
        return params.data.IS_LINKED ? 'is-linked select-with-icon' : 'select-with-icon';
      },
      cellRendererSelector: (params) => {
        const icon = {
          component: IconCellRendered,
        };

        if (params.data) {
          return icon;
        }
        return undefined;
      },
    },
    {
      field: COLUMN_FIELDS.TICKER,
      headerName: 'Ticker',
      headerCheckboxSelection: false,
      flex: 1,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
    },
    {
      field: COLUMN_FIELDS.UNIQUE_ID,
      headerName: 'RFQ ID',
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.ISO,
      headerName: 'ISO',
      filter: true,
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      headerName: 'Fixed Price (MW/h)',
      field: 'PRICE',
      flex: 1,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        filterOptions: [
          {
            displayKey: 'lessThanOrEqual',
            displayName: '<= Upper Bound',
            predicate: ([fv1], cellValue) => filterLessOrEqualNumberic(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'greaterThanOrEqual',
            displayName: '>= Lower Bound',
            predicate: ([fv1], cellValue) => filterGreaterOrEqualNumberic(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'inRange',
            displayName: 'In range',
            predicate: ([fv1, fv2], cellValue) => filterNumericRange([fv1, fv2], cellValue),
            numberOfInputs: 2,
          },
        ],
        suppressAndOrCondition: true,
      },
      valueGetter: (params) => {
        if (params.data.RFQ.PRODUCT_TYPE === PRODUCT_TYPE.RECS) {
          return [params.data.RFQ.FIXED_PRICE, params.data.RFQ.FIXED_PRICE];
        }
        return [params.data.RFQ.FIXED_PRICE_LOWER_BOUND, params.data.RFQ.FIXED_PRICE_UPPER_BOUND];
      },
      valueFormatter: function (params) {
        if (params.data.RFQ.PRODUCT_TYPE === PRODUCT_TYPE.RECS) {
          return '$' + params.data.RFQ.FIXED_PRICE;
        }
        return (
          '$' +
          params.data.RFQ.FIXED_PRICE_LOWER_BOUND +
          ' - ' +
          params.data.RFQ.FIXED_PRICE_UPPER_BOUND
        );
      },
    },
    {
      field: COLUMN_FIELDS.VOLUME,
      headerName: 'Volume (MW)',
      filter: 'agNumberColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      valueFormatter: (params) => getFormattedNumber(params.data.RFQ.REQUESTED_VOLUME),
    },
    {
      field: COLUMN_FIELDS.TENOR_START_DATE,
      headerName: 'Start Date',
      valueFormatter: (params) => getFormattedDate(params.data.RFQ.TENOR_START_DATE),
      filter: 'agDateColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        comparator: (filterLocalDateAtMidnight: Date, cellValue) => {
          const date = new Date(
            Date.UTC(
              filterLocalDateAtMidnight.getFullYear(),
              filterLocalDateAtMidnight.getMonth(),
              filterLocalDateAtMidnight.getDate()
            )
          );

          if (moment(cellValue).isBefore(date, 'day')) {
            return -1;
          } else if (moment(cellValue).isAfter(date, 'day')) {
            return 1;
          }
          return 0;
        },
        browserDatePicker: true,
        buttons: ['apply', 'reset'],
      },
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.TENOR_MONTHS,
      headerName: 'Tenor(mo)',
      filter: 'agNumberColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.ACTIONS,
      suppressMenu: true,
      headerName: '',
      width: 55,
      cellRenderer: ActionCellRenderer,
      cellRendererParams: (params) => {
        const compareBtn = {
          name: listIcon(),
          callback: (detail) => {
            context.summaryData = { vppaData: context.selectedVppa, rfqData: detail.RFQ };
            context.isSummaryOpen = true;
            context.rfqCompanyName = detail.COMPANY_NAME;
          },
          type: ActionTypes.COMPARE,
          actionName: 'compare-rfq',
        };
        const actions = [compareBtn];
        const enabled = true;
        return {
          actions,
          enabled,
        };
      },
    },
  ];
}
